/**
 * Sentry 에러 모니터링 통합 모듈
 *
 * 프로덕션 환경에서만 활성화됩니다.
 * Capacitor 네이티브 + React 통합을 지원합니다.
 */
import * as Sentry from "@sentry/capacitor";
import * as SentryReact from "@sentry/react";
import { createLogger } from "@ict/shared";

const logger = createLogger({ module: "Sentry" });

/**
 * Sentry 초기화
 * 가능한 빨리 호출해야 하므로 main.tsx에서 동기적으로 실행합니다.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!import.meta.env.PROD) {
    logger.debug("개발 환경 - Sentry 비활성화");
    return;
  }

  if (!dsn) {
    logger.warn("VITE_SENTRY_DSN이 설정되지 않음 - Sentry 비활성화");
    return;
  }

  try {
    Sentry.init(
      {
        dsn,
        environment: import.meta.env.MODE,
        enabled: import.meta.env.PROD,
        tracesSampleRate: 0.2,
        integrations: [SentryReact.browserTracingIntegration()],
      },
      SentryReact.init
    );
    logger.info("Sentry 초기화 완료");
  } catch (error) {
    logger.warn("Sentry 초기화 실패", error);
  }
}

/**
 * Sentry 사용자 컨텍스트 설정
 */
export function setSentryUser(user: { id: string; email?: string; username?: string }): void {
  if (!import.meta.env.PROD) {
    logger.debug(`[Sentry] 사용자 설정: ${user.id}`);
    return;
  }

  Sentry.setUser(user);
}

/**
 * Sentry React ErrorBoundary 래퍼
 */
export const SentryErrorBoundary = SentryReact.ErrorBoundary;
